import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { dbHelpers } from '../../lib/supabase';
// Removed problematic import for Vercel deployment
// import { parsedSessionsData } from '../../data';
import './UrlModerator.css';

const UrlModerator = ({ user }) => {
  const [pendingUrls, setPendingUrls] = useState([]);
  const [allUrls, setAllUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [moderationNotes, setModerationNotes] = useState({});
  const [processingIds, setProcessingIds] = useState(new Set());
  const [sessionDetails, setSessionDetails] = useState({});

  const loadUrls = useCallback(async () => {
    try {
      setLoading(true);

      // Load pending URLs
      const { data: pending, error: pendingError } = await dbHelpers.getPresenterUrls(null, 'pending');
      if (pendingError) {
        console.error('Error loading pending URLs:', pendingError);
        setError('Failed to load pending URLs');
      } else {
        setPendingUrls(pending || []);
        await loadSessionDetails(pending || []);
      }

      // Load all URLs for the "All URLs" tab
      const { data: all, error: allError } = await dbHelpers.getPresenterUrls();
      if (allError) {
        console.error('Error loading all URLs:', allError);
        setError('Failed to load all URLs');
      } else {
        setAllUrls(all || []);
        await loadSessionDetails(all || []);
      }
    } catch (err) {
      console.error('Error loading URLs:', err);
      setError('Failed to load URLs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUrls();
  }, [loadUrls]);

  const handleModeration = async (urlId, status) => {
    setProcessingIds(prev => new Set([...prev, urlId]));
    setError('');
    setSuccess('');

    try {
      const notes = moderationNotes[urlId] || '';
      const { success: moderationSuccess, error: moderationError } = await dbHelpers.moderatePresenterUrl(
        urlId,
        status,
        notes
      );

      if (moderationSuccess) {
        setSuccess(`URL ${status} successfully!`);
        // Clear the moderation notes for this URL
        setModerationNotes(prev => {
          const updated = { ...prev };
          delete updated[urlId];
          return updated;
        });
        // Reload URLs
        await loadUrls();
      } else {
        setError(moderationError?.message || `Failed to ${status} URL`);
      }
    } catch (err) {
      console.error('Error moderating URL:', err);
      setError('An unexpected error occurred');
    } finally {
      setProcessingIds(prev => {
        const updated = new Set(prev);
        updated.delete(urlId);
        return updated;
      });
    }
  };

  const handleDelete = async (urlId) => {
    if (!window.confirm('Are you sure you want to delete this URL? This action cannot be undone.')) {
      return;
    }

    setProcessingIds(prev => new Set([...prev, urlId]));
    setError('');
    setSuccess('');

    try {
      const { success: deleteSuccess, error: deleteError } = await dbHelpers.deletePresenterUrl(urlId);

      if (deleteSuccess) {
        setSuccess('URL deleted successfully!');
        await loadUrls();
      } else {
        setError(deleteError?.message || 'Failed to delete URL');
      }
    } catch (err) {
      console.error('Error deleting URL:', err);
      setError('An unexpected error occurred');
    } finally {
      setProcessingIds(prev => {
        const updated = new Set(prev);
        updated.delete(urlId);
        return updated;
      });
    }
  };

  const handleNotesChange = (urlId, notes) => {
    setModerationNotes(prev => ({
      ...prev,
      [urlId]: notes
    }));
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      approved: 'status-approved',
      rejected: 'status-rejected'
    };

    return (
      <span className={`status-badge ${statusClasses[status] || ''}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const loadSessionDetails = async (urls) => {
    const sessionIds = [...new Set(urls.map(url => url.session_id))];
    const details = {};

    for (const sessionId of sessionIds) {
      try {
        // Try to get from database first
        const { data: dbSession } = await dbHelpers.getSession(sessionId);
        if (dbSession) {
          details[sessionId] = dbSession;
        }
        // Fallback removed for Vercel deployment compatibility
      } catch (error) {
        console.error(`Error loading session ${sessionId}:`, error);
        // Fallback removed for Vercel deployment compatibility
      }
    }

    setSessionDetails(details);
  };

  const renderUrlCard = (urlData) => {
    const isProcessing = processingIds.has(urlData.id);

    return (
      <div key={urlData.id} className="url-card">
        <div className="url-header">
          <div className="url-info">
            <h4>Session ID: {urlData.session_id}</h4>
            {getStatusBadge(urlData.status)}
          </div>
          <div className="url-meta">
            <span className="submitted-date">Submitted: {formatDate(urlData.created_at)}</span>
          </div>
        </div>

        {sessionDetails[urlData.session_id] && (
          <div className="session-info">
            <h5>Session Details:</h5>
            <div className="session-details">
              <div className="session-title">
                <strong>Title:</strong> {sessionDetails[urlData.session_id].title}
              </div>
              {sessionDetails[urlData.session_id].speaker && (
                <div className="session-speaker">
                  <strong>Speaker(s):</strong> {sessionDetails[urlData.session_id].speaker}
                </div>
              )}
              {sessionDetails[urlData.session_id].track && (
                <div className="session-track">
                  <strong>Track:</strong> {sessionDetails[urlData.session_id].track}
                </div>
              )}
              {sessionDetails[urlData.session_id].time && (
                <div className="session-time">
                  <strong>Time:</strong> {sessionDetails[urlData.session_id].time}
                </div>
              )}
              {sessionDetails[urlData.session_id].category && (
                <div className="session-category">
                  <strong>Category:</strong> {sessionDetails[urlData.session_id].category}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="url-content">
          <div className="presenter-info">
            <strong>Presenter:</strong> {urlData.presenter_name} ({urlData.presenter_email})
          </div>

          <div className="url-link">
            <strong>URL:</strong>
            <a href={urlData.url} target="_blank" rel="noopener noreferrer">
              {urlData.url}
            </a>
          </div>

          {urlData.description && (
            <div className="url-description">
              <strong>Description:</strong> {urlData.description}
            </div>
          )}

          {urlData.moderated_at && (
            <div className="moderation-info">
              <strong>Moderated:</strong> {formatDate(urlData.moderated_at)}
              {urlData.moderation_notes && (
                <div className="moderation-notes">
                  <strong>Notes:</strong> {urlData.moderation_notes}
                </div>
              )}
            </div>
          )}
        </div>

        {urlData.status === 'pending' && (
          <div className="moderation-actions">
            <div className="notes-section">
              <label htmlFor={`notes-${urlData.id}`}>Moderation Notes (Optional):</label>
              <textarea
                id={`notes-${urlData.id}`}
                value={moderationNotes[urlData.id] || ''}
                onChange={(e) => handleNotesChange(urlData.id, e.target.value)}
                placeholder="Add any notes about this moderation decision..."
                rows="2"
                disabled={isProcessing}
              />
            </div>

            <div className="action-buttons">
              <button
                className="approve-btn"
                onClick={() => handleModeration(urlData.id, 'approved')}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Approve'}
              </button>
              <button
                className="reject-btn"
                onClick={() => handleModeration(urlData.id, 'rejected')}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Reject'}
              </button>
            </div>
          </div>
        )}

        {urlData.status !== 'pending' && (
          <div className="admin-actions">
            <button
              className="delete-btn"
              onClick={() => handleDelete(urlData.id)}
              disabled={isProcessing}
            >
              {isProcessing ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="url-moderator">
        <div className="loading-message">Loading URLs...</div>
      </div>
    );
  }

  return (
    <div className="url-moderator">
      <div className="moderator-header">
        <h2>URL Moderation</h2>
        <p>Review and moderate presenter-submitted URLs</p>
      </div>

      {error && <div className="error-message">{typeof error === 'object' ? (error.message || JSON.stringify(error)) : String(error)}</div>}
      {success && <div className="success-message">{typeof success === 'object' ? (success.message || JSON.stringify(success)) : String(success)}</div>}

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending ({pendingUrls.length})
        </button>
        <button
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All URLs ({allUrls.length})
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'pending' && (
          <div className="pending-urls">
            {pendingUrls.length === 0 ? (
              <div className="no-urls-message">
                <p>No pending URLs to review.</p>
              </div>
            ) : (
              <div className="urls-list">
                {pendingUrls.map(renderUrlCard)}
              </div>
            )}
          </div>
        )}

        {activeTab === 'all' && (
          <div className="all-urls">
            {allUrls.length === 0 ? (
              <div className="no-urls-message">
                <p>No URLs have been submitted yet.</p>
              </div>
            ) : (
              <div className="urls-list">
                {allUrls.map(renderUrlCard)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

UrlModerator.propTypes = {
  user: PropTypes.object
};

export default UrlModerator;